import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import RadarButton from "@/components/RadarButton";
import { useAuth } from "@/lib/auth";

const typing = (el) => !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA");

// Public result only (before signup): fixed CTA. Hidden while typing, while the "Où es-tu" block or the footer is on screen,
// and once signed up. A spacer of its height + 24 px ends the page so nothing stays hidden behind it.
export default function StickyUnlock({ onUnlock }) {
  const { user } = useAuth();
  const ref = useRef(null);
  const [h, setH] = useState(0);
  const [focus, setFocus] = useState(false);
  const [guard, setGuard] = useState(false);

  useEffect(() => {
    const on = (e) => setFocus(typing(e.target));
    const off = () => setTimeout(() => setFocus(typing(document.activeElement)), 0);
    document.addEventListener("focusin", on);
    document.addEventListener("focusout", off);
    return () => {
      document.removeEventListener("focusin", on);
      document.removeEventListener("focusout", off);
    };
  }, []);

  useEffect(() => {
    const seen = new Set();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting ? seen.add(e.target) : seen.delete(e.target)));
      setGuard(seen.size > 0);
    });
    document.querySelectorAll("[data-cta-guard]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  });

  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(() => setH(ref.current?.offsetHeight || 0));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  if (user) return null;
  const hidden = focus || guard;
  return (
    <>
      <div aria-hidden="true" style={{ height: h + 24 }} data-testid="unlock-cta-spacer" />
      {createPortal(
      <div
        ref={ref}
        data-testid="unlock-cta"
        data-hidden={hidden}
        className={`fixed inset-x-4 bottom-[calc(16px+env(safe-area-inset-bottom))] z-30 flex flex-col items-center gap-2 transition-[opacity,transform] duration-200 md:inset-x-auto md:bottom-6 md:right-6 md:w-[360px] ${
          hidden ? "pointer-events-none translate-y-3 opacity-0" : "opacity-100"
        }`}
      >
        <p className="rounded-full bg-bg/90 px-3 py-0.5 text-[13px] text-muted" data-testid="unlock-cta-sub">Un compte suffit. 30 secondes avec Google.</p>
        <RadarButton onClick={onUnlock} data-testid="unlock-btn" className="w-full" tabIndex={hidden ? -1 : 0}>
          Débloquer les 3 zones
        </RadarButton>
      </div>,
      document.body,
      )}
    </>
  );
}
